'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useId } from 'react';
import { MediaPlayer, useMediaPlayer, useOnPlay, type MediaPlayerAssetVideo, type MediaPlayerCaption, type MediaPlayerCustomizations } from '@udemy-v2/react-media-player';
import type { AssetVideo as GraphQLAssetVideo, AssetVideoMashup as GraphQLAssetVideoMashup } from '@/generated/graphql';
export interface MediaPlayerWrapperProps {
  asset?: GraphQLAssetVideo | GraphQLAssetVideoMashup;
  /** Called when playback starts, with the current playback position in seconds */
  onPlay?: (currentTime: number) => void;
  config?: MediaPlayerCustomizations;
}

/**
 * Media player that accepts a GraphQL AssetVideo and handles all react-media-player
 * wiring: store registration, event subscription, and mapping asset captions/thumbnails
 * to MediaPlayer props.
 *
 * Uses the global MediaPlayerProvider from CoreProviders (same as VideoPlayer) so that
 * Shaka is initialized once and reused — avoiding WHEN_TIMEOUT errors that occur when
 * a per-mount provider creates a fresh ShakaPlayerLoader on each React Strict Mode
 * cleanup cycle.
 *
 * Does not contain any domain logic — accepts any GraphQL AssetVideo and can be
 * reused across contexts.
 */
export function MediaPlayerWrapper(t0) {
  const $ = _c(15);
  const {
    asset,
    onPlay,
    config
  } = t0;
  const fallbackId = useId();
  const playerId = asset ? `clp-preview-player-${asset.id}` : fallbackId;
  const mediaPlayer = useMediaPlayer(playerId, config);
  let t1;
  let t2;
  if ($[0] !== asset || $[1] !== mediaPlayer) {
    t1 = () => {
      if (asset && !mediaPlayer.isLoadingMedia) {
        mediaPlayer.loadAsset(asset as unknown as MediaPlayerAssetVideo);
      }
    };
    t2 = [asset, mediaPlayer];
    $[0] = asset;
    $[1] = mediaPlayer;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[4] !== mediaPlayer || $[5] !== onPlay) {
    t3 = () => {
      if (onPlay) {
        onPlay(mediaPlayer.currentTime);
      }
    };
    t4 = [onPlay, mediaPlayer];
    $[4] = mediaPlayer;
    $[5] = onPlay;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useOnPlay(playerId, t3, t4);
  let t5;
  if ($[8] !== asset?.captions) {
    t5 = asset?.captions?.filter(_temp).map(_temp2);
    $[8] = asset?.captions;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const captions = t5;
  const t6 = asset?.thumbnailTrack?.imgUrl;
  const t7 = asset?.thumbnailTrack?.vttUrl;
  let t8;
  if ($[10] !== captions || $[11] !== playerId || $[12] !== t6 || $[13] !== t7) {
    t8 = <MediaPlayer id={playerId} captions={captions} thumbSpriteUrl={t6} thumbnailsVTTUrl={t7} />;
    $[10] = captions;
    $[11] = playerId;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  return t8;
}
function _temp2(c_0) {
  return {
    id: Number(c_0.id),
    language: c_0.language,
    source: c_0.source,
    status: c_0.status,
    title: c_0.videoLabel,
    url: c_0.url,
    videoLabel: c_0.videoLabel
  };
}
function _temp(c) {
  return c.status === "SUCCESS";
}